import { z } from 'zod'
import { getMcpInputSchema } from '../sidebar/mcp-tool/mcp-tool-config'
import { isPlainObject } from '../sidebar/mcp-tool/mcp-tool-json-schema'
import { missingInputs } from '../sidebar/mcp-tool/mcp-tool-missing-inputs'

const requiredString = (message: string) =>
  z
    .string()
    .nullish()
    .refine((value) => !!value?.trim(), message)

export const mcpAppValidation = {
  app_provider: requiredString('Select an app'),
  org_app_uuid: requiredString('Select an app'),
  tool_name: requiredString('Select a tool'),
}

export const mcpCodeValidation = {
  code_name: requiredString('Function name is required'),
  code_description: requiredString('Function description is required'),
  code_body: requiredString('Code is required'),
  code_function_schema: z
    .unknown()
    .refine(
      (value) => isPlainObject(value) && isPlainObject(value.parameters),
      'Function schema is required. Please generate.',
    ),
}

export const mcpToolFields = {
  source: z.enum(['app', 'custom_code']).optional(),
  app_provider: z.string().nullish(),
  org_app_uuid: z.string().nullish(),
  app_name: z.string().nullish(),
  tool_name: z.string().nullish(),
  tool_schema: z
    .object({
      properties: z.record(z.unknown()).optional(),
      required: z.array(z.string()).optional(),
    })
    .passthrough()
    .nullish(),
  code_name: z.string().nullish(),
  code_description: z.string().nullish(),
  code_body: z.string().nullish(),
  code_function_schema: z.unknown(),
}

type McpConfig = z.infer<z.ZodObject<typeof mcpToolFields>> & { inputs: Record<string, string> }

export const validateMcpTool = (data: McpConfig, ctx: z.RefinementCtx) => {
  const fields = data.source === 'custom_code' ? mcpCodeValidation : mcpAppValidation
  for (const [key, schema] of Object.entries(fields)) {
    const result = schema.safeParse(data[key as keyof McpConfig])
    if (!result.success) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: result.error.issues[0].message,
        path: [key],
      })
    }
  }
  for (const { key, message } of missingInputs(getMcpInputSchema(data), data.inputs ?? {})) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message, path: ['inputs', key] })
  }
}
